import { FormEvent, useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { api } from "../../../../api";
import { config } from "../../../../config";
import { usePoolsV2ContractAddress } from "../../../../hooks/usePoolsV2ContractAddress";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { openTfaModal } from "../../../../redux/features/uiSlice";
import {
  resetTfaAuthorizationForAction,
  setCurrentTfaAction,
} from "../../../../redux/features/userSlice";
import { poolsContractService } from "../../../../services";
import { toast } from "../../../../toasts";
import { handleBlockchainPromiseInToast } from "../../../../toasts/handleBlockchainPromiseInToast";
import { formatBigNumber, useEthersSigner } from "../../../../utils";
import { useTokenData } from "../../../../hooks/useTokenData";

export const usePoolClaimHandler = (
  poolLevel: PoolLevel,
  onSuccess: () => void
) => {
  const [isClaimInProgress, setIsClaimInProgress] = useState(false);
  const signer = useEthersSigner();
  const dispatch = useAppDispatch();
  const { chainId } = useAccount();
  const poolsV2ContractAddress = usePoolsV2ContractAddress();
  const { decimals: usdcTokenDecimals, symbol } = useTokenData("USDC");

  const isV2Contract = poolLevel.pool.version === "v2";

  const { tfaEnabled, tfaAuthenticatedForAction } = useAppSelector(
    (state) => state.user
  );
  const { isPoolsClaimAuthenticated } = tfaAuthenticatedForAction;

  const claimRewards = async () => {
    try {
      setIsClaimInProgress(true);

      if (!signer) {
        throw new Error("Wallet not connected");
      }

      if (!chainId) return;

      const rewards = poolLevel.usdcReward || "0";
      const soilRewards = poolLevel.soilReward || "0";

      const {
        data: { signature, deadline, rawData },
      } = await api.pools.getClaimRewardsSignature({
        poolId: poolLevel.pool.id,
        level: poolLevel.level,
        rewards,
        soilRewards,
        withWithdraw: false,
        unlockId: 0,
      });

      const {
        poolId,
        level,
        rewards: rewardsToBlockchain,
        soilRewards: soilRewardsToBlockchain,
        userAddress,
      } = rawData;

      let claimTx;

      if (isV2Contract) {
        claimTx = poolsContractService.claimRewardsV2(
          poolId,
          level,
          rewardsToBlockchain,
          soilRewardsToBlockchain,
          deadline,
          signature,
          signer,
          poolsV2ContractAddress,
          userAddress
        );
      } else {
        claimTx = poolsContractService.claimRewards(
          poolLevel.pool.poolId,
          poolLevel.level,
          rewards,
          soilRewards,
          deadline,
          signature,
          signer
        );
      }

      const formattedUSDCRewards = formatBigNumber(rewards, usdcTokenDecimals);
      const formattedSoilRewards = formatBigNumber(
        soilRewards,
        config.SOIL_TOKEN_DECIMALS
      );

      const title = `Claiming of ${formattedUSDCRewards} ${symbol} ${
        chainId === config.NETWORK_ID &&
        `and ${formattedSoilRewards} ${config.PROJECT_TOKEN}`
      }...`;

      await handleBlockchainPromiseInToast(
        claimTx,
        {
          pending: {
            title,
            message: "Your request is in progress...",
          },
          error: {
            title,
            message:
              "Your request has been finished unsuccessful. Try again later",
          },
          success: {
            title,
            message:
              "The transaction has been finished successful. You can check details on the block explorer by click the button below.",
          },
        },
        chainId
      );

      onSuccess();
    } catch (error: unknown) {
      toast.errorHandler(error, "Failed to claim rewards!");
      setIsClaimInProgress(false);
      dispatch(resetTfaAuthorizationForAction());
    }
  };

  const submitClaimRewards = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (tfaEnabled) {
      dispatch(setCurrentTfaAction("claim"));
      dispatch(openTfaModal());
    } else {
      claimRewards();
    }
  };

  useEffect(() => {
    if (isPoolsClaimAuthenticated) {
      claimRewards();
    }
  }, [isPoolsClaimAuthenticated]);

  return { isClaimInProgress, submitClaimRewards };
};
